import { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorCheckbox from '~/components/editor/EditorCheckbox/EditorCheckbox';
import EditorNumber from '~/components/editor/EditorNumber/EditorNumber';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import EditorText from '~/components/editor/EditorText/EditorText';
import Checkbox from '~/components/widgets/Checkbox/Checkbox';
import Collapsible from '~/components/widgets/Collapsible/Collapsible';
import {
  channelCanConfigureDetuning,
  isDeviceConfigurable
} from '~/models/device';
import { Basis } from '~/models/studio';
import { useExperiment } from '~/systems/experiment';
import type { Channel } from '~/models/studio';
import './ChannelAdd.css';

const basisOptions = [
  { label: 'Digital', value: Basis.Digital },
  { label: 'Ground Rydberg', value: Basis.GroundRydberg }
];

const addressingOptions = [
  { label: 'Local', value: 'local' },
  { label: 'Global', value: 'global' }
];

export default function ChannelAdd() {
  const { channels, device, openTransaction } = useExperiment();
  const [collapsibleOpened, setCollapsibleOpened] = useState(false);
  const collapsibleRef = useRef<{ open(): void; close(): void }>(null);

  const canCreateChannel = isDeviceConfigurable(device);

  function defaultData(): Omit<Channel, 'uuid'> {
    return {
      label: `ch${channels.length}`,
      addressing: 'local',
      basis: Basis.Digital,
      maxAbsDetuning: 125.664,
      maxAmp: 62.832,
      phaseJumpTime: 0,
      minRetargetInterval: 220,
      fixedRetargetT: 0,
      maxTargets: 1,
      clockPeriod: 4,
      minDuration: 16,
      maxDuration: undefined,
      modBandwidth: undefined,
      enableDetuning: false
    };
  }

  const [channel, setChannel] = useState<Omit<Channel, 'uuid'>>(defaultData);

  function updateChannel(patch: Partial<Omit<Channel, 'uuid'>>) {
    setChannel((prev) => ({ ...prev, ...patch }));
  }

  function onCreateChannel() {
    const ch = channel;
    if (ch.addressing === 'global') {
      updateChannel({ enableDetuning: true });
    }
    // Use the current state snapshot (closure), create with enableDetuning adjusted
    const finalChannel: Channel = {
      uuid: uuid(),
      ...ch,
      enableDetuning: ch.addressing === 'global' ? true : ch.enableDetuning
    };
    const tx = openTransaction('Create Channel');
    tx.push({ kind: 'add_channel', channel: finalChannel, insertBefore: null });
    tx.commit();
    setChannel(defaultData());
  }

  if (!canCreateChannel) {
    return null;
  }

  return (
    <div
      className={`ChannelAdd-channelControl u-scrollbarDark${collapsibleOpened ? ' is-opened' : ''}`}
    >
      <Collapsible
        ref={collapsibleRef}
        initialState={false}
        subcollapsible
        onUpdateOpened={setCollapsibleOpened}
        button={() => <h3 className="ChannelAdd-title">Add Channel</h3>}
        items={
          <div className="ChannelAdd-body">
            {isDeviceConfigurable(device) &&
              channelCanConfigureDetuning(channel) && (
                <EditorCheckbox
                  label="Detuning"
                  name="enable-detuning"
                  value={channel.enableDetuning ?? false}
                  onChange={(v) => updateChannel({ enableDetuning: v })}
                />
              )}
            <EditorText
              label="Label"
              name="channel-label"
              value={channel.label}
              onChange={(v) => updateChannel({ label: v })}
            />
            <EditorSelect
              label="Basis"
              name="basis"
              value={channel.basis}
              onChange={(v) => updateChannel({ basis: v as Basis })}
              options={basisOptions}
            />
            <EditorSelect
              label="Addressing"
              name="addressing"
              value={channel.addressing}
              onChange={(v) =>
                updateChannel({ addressing: v as 'local' | 'global' })
              }
              options={addressingOptions}
            />
            <EditorNumber
              label="Max Abs Detuning"
              name="max-abs-detuning"
              value={channel.maxAbsDetuning}
              onChange={(v) => updateChannel({ maxAbsDetuning: v })}
              step={0.001}
              max={Infinity}
              min={0.001}
              unit="rad/µs"
            />
            <EditorNumber
              label="Max Amp"
              name="max-amp"
              value={channel.maxAmp}
              onChange={(v) => updateChannel({ maxAmp: v })}
              step={0.001}
              max={Infinity}
              min={0.001}
              unit="rad/µs"
            />
            <EditorNumber
              label="Phase Jump Time"
              name="phase-jump-time"
              value={channel.phaseJumpTime}
              onChange={(v) => updateChannel({ phaseJumpTime: v })}
              step={0.001}
              max={Infinity}
              min={0}
              unit="ns"
            />
            <EditorNumber
              label="Clock Period"
              name="clock-period"
              value={channel.clockPeriod}
              onChange={(v) => updateChannel({ clockPeriod: v })}
              step={0.001}
              max={Infinity}
              min={0}
              unit="ns"
            />
            <EditorNumber
              label="Min Duration"
              name="min-duration"
              value={channel.minDuration}
              onChange={(v) => updateChannel({ minDuration: v })}
              step={1}
              max={Infinity}
              min={1}
              unit="ns"
            />
            <div className="ChannelAdd-optionalFieldRow">
              <EditorNumber
                label="Min Retarget Interval"
                name="min-retarget-interval"
                value={channel.minRetargetInterval ?? 0}
                onChange={(v) => updateChannel({ minRetargetInterval: v })}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={channel.minRetargetInterval === undefined}
              />
              <Checkbox
                value={channel.minRetargetInterval !== undefined}
                onChange={(v) =>
                  updateChannel({ minRetargetInterval: v ? 0 : undefined })
                }
              />
            </div>
            <div className="ChannelAdd-optionalFieldRow">
              <EditorNumber
                label="Fixed Retarget Time"
                name="fixed-retarget-time"
                value={channel.fixedRetargetT ?? 0}
                onChange={(v) => updateChannel({ fixedRetargetT: v })}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={channel.fixedRetargetT === undefined}
              />
              <Checkbox
                value={channel.fixedRetargetT !== undefined}
                onChange={(v) =>
                  updateChannel({ fixedRetargetT: v ? 0 : undefined })
                }
              />
            </div>
            <div className="ChannelAdd-optionalFieldRow">
              <EditorNumber
                label="Max Targets"
                name="max-targets"
                value={channel.maxTargets ?? 0}
                onChange={(v) => updateChannel({ maxTargets: v })}
                step={1}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={channel.maxTargets === undefined}
              />
              <Checkbox
                value={channel.maxTargets !== undefined}
                onChange={(v) =>
                  updateChannel({ maxTargets: v ? 0 : undefined })
                }
              />
            </div>
            <div className="ChannelAdd-optionalFieldRow">
              <EditorNumber
                label="Max Duration"
                name="mas-duration"
                value={channel.maxDuration ?? 0}
                onChange={(v) => updateChannel({ maxDuration: v })}
                step={1}
                max={10000}
                min={1}
                unit="ns"
                disabled={channel.maxDuration === undefined}
              />
              <Checkbox
                value={channel.maxDuration !== undefined}
                onChange={(v) =>
                  updateChannel({ maxDuration: v ? 1 : undefined })
                }
              />
            </div>
            <div className="ChannelAdd-optionalFieldRow">
              <EditorNumber
                label="Mod Bandwidth"
                name="mod-bandwidth"
                value={channel.modBandwidth ?? 0}
                onChange={(v) => updateChannel({ modBandwidth: v })}
                step={0.001}
                max={Infinity}
                min={0}
                unit="ns"
                disabled={channel.modBandwidth === undefined}
              />
              <Checkbox
                value={channel.modBandwidth !== undefined}
                onChange={(v) =>
                  updateChannel({ modBandwidth: v ? 0 : undefined })
                }
              />
            </div>
            <div className="ChannelAdd-addButtonRow">
              <EditorButton kind="primary" onClick={onCreateChannel}>
                Add Channel
              </EditorButton>
            </div>
          </div>
        }
      />
    </div>
  );
}
